/**
 * Billing Plan / Pricing Configuration — Phase 2 (Stripe).
 *
 * Source of truth for the per-employee pricing model:
 *   fixed base fee + graduated per-employee overage.
 * See PRD/PRD_BillingPhase2.md ("Pricing model" + "Price configuration").
 *
 * Pricing shape (two Stripe Products on one subscription):
 *   - BASE: a flat ₱BILLING_FLOOR/month, quantity fixed at 1. Always charged —
 *     this is the structural floor (a 0-employee cycle still bills the floor).
 *   - OVERAGE: graduated, charged on employees ABOVE `INCLUDED`. The bands below
 *     are expressed in *employee-count* space; the Stripe overage Price's tier
 *     `up_to` values are derived as `band.upTo − INCLUDED` (done in
 *     functions/src/config/stripePrices.js, which boot-verifies the coupling).
 *
 * Worked totals (base + overage): 0–20 → ₱5,000 (floor); 25 → ₱5,750;
 * 30 → ₱6,500; 50 → ₱9,500; 100 → ₱15,750; 150 → ₱20,750.
 *
 * Values are PLACEHOLDERS for v1 (owner A/B-tests final numbers separately);
 * the *structure* is in scope. Amounts are whole pesos (Stripe stores centavos).
 *
 * MUST stay in sync with functions/src/config/plans.js — gated by
 * scripts/check-plans-parity.js.
 */

// ============================================================
// PRICING CONFIG
// ============================================================

/** Flat monthly base fee, in pesos. Covers the first INCLUDED employees. */
export const BILLING_FLOOR = 5000;

/** Employees covered by the base fee. Overage applies only above this count. */
export const INCLUDED = 20;

/**
 * Graduated overage bands, in EMPLOYEE-COUNT space, ordered ascending.
 *  - `upTo`: inclusive employee-count upper bound for the band (null = unbounded).
 *  - `amount`: per-employee charge (pesos/month) for employees in this band.
 *
 * The first band starts just above INCLUDED. e.g. with INCLUDED=20:
 *   employees 21–50 → ₱150, 51–100 → ₱125, 101+ → ₱100.
 */
export const BANDS = [
  { upTo: 50, amount: 150 },
  { upTo: 100, amount: 125 },
  { upTo: null, amount: 100 },
];

// ============================================================
// ESTIMATE / BILL COMPUTATION
// ============================================================

/**
 * Compute the monthly bill for a given active-employee count.
 *
 * The base fee is always charged; overage accrues per band for employees above
 * INCLUDED. Used by the dashboard Billing-estimate card (fed the running cycle
 * max, NOT raw live count — see PRD "Which count where") and mirrored
 * server-side for the boot-verify and webhook math.
 *
 * @param {number} activeCount - Active-employee count to price.
 * @return {{ total:number, base:number, overage:number, breakdown:Array }}
 *   `breakdown` entries: { from, to, count, rate, amount } per non-empty band.
 */
export function computeBillEstimate(activeCount) {
  const n = Math.max(0, Math.floor(Number(activeCount) || 0));
  let lower = INCLUDED; // employees up to INCLUDED are covered by the base fee
  let overage = 0;
  const breakdown = [];

  for (const band of BANDS) {
    if (n <= lower) break;
    const upper = band.upTo == null ? Infinity : band.upTo;
    const count = Math.min(n, upper) - lower;
    if (count > 0) {
      const amount = count * band.amount;
      overage += amount;
      breakdown.push({
        from: lower + 1,
        to: band.upTo, // null = "and above"
        count,
        rate: band.amount,
        amount,
      });
    }
    lower = upper;
  }

  return {
    total: BILLING_FLOOR + overage,
    base: BILLING_FLOOR,
    overage,
    breakdown,
  };
}